// The Stüdyo tab of /influio-tv — one button. RECORD writes the Script for the chosen Sales
// Day, puts it on the prompter while Lia's lines are voiced, then rolls tape and she performs
// it; the Take is previewed and saved as an Episode, or discarded. docs/influio-tv/PLAN.md
// in the server repo.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, CardContent, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DeleteOutline, FiberManualRecord, SaveOutlined, StopCircleOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import { fetchImageBlob, generateScript, listEpisodes, saveEpisode, KARACA_OFFER_ID } from '../../../services/influioTvService';
import { createStudioScene } from './scene';
import { fetchVoice, mouthLevel, playVoice, stopVoice, voiceSeconds, voiceStream } from './voice';
import { canTapeMp4, isRecording, startRecording, stopRecording } from './record';
import styles from './InfluioTvStudio.module.scss';

const KIND_LABEL = {
    opening: 'Açılış', totals: 'Toplamlar', products: 'Ürünler', influencers: 'Influencerlar', tips: 'Amplify Tips', closing: 'Kapanış',
};
const LEAD_IN_MS = 900;      // the stage settles before the first word
const GAP_MS = 450;          // breath between Segments
const TAIL_MS = 1400;        // hold the closing board before the tape stops
const MAX_MB = 70;           // the server's upload cap; record.js budgets the bitrate to stay under it
const TL = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
const pct = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// ponytail: what the producer sees while Lia works is a glimpse, never the Script — rotating
// one-liners while she writes, the first few words of a Segment while it is voiced or on air.
const WRITING_PEEKS = [
    'dünün satış rakamlarını açıyor', 'önceki günle kıyaslıyor', 'tıklama ve dönüşüme bakıyor',
    'en çok satan üçlüyü sıralıyor', 'influencer tablosunu tarıyor', 'iki Amplify Tip seçiyor',
    'açılış cümlesini yazıyor', 'kapanışı toparlıyor',
];
const peek = (text, n = 9) => { const w = (text || '').trim().split(/\s+/); return w.slice(0, n).join(' ') + (w.length > n ? '…' : ''); };
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
// product photos for the wall screen come through the server (the CDN has no CORS)
const loadImage = async (src) => {
    const img = new Image();
    img.src = URL.createObjectURL(await fetchImageBlob(src));
    await img.decode();
    return img;
};

const InfluioTvStudio = () => {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const runRef = useRef(0);

    const [salesDay, setSalesDay] = useState(dayjs().subtract(1, 'day'));
    const [notes, setNotes] = useState('');
    const [facts, setFacts] = useState(null);
    const [title, setTitle] = useState('');
    const [segments, setSegments] = useState([]);
    const [busy, setBusy] = useState(null); // 'script' | 'voices' | 'recording' | 'saving'
    const [progress, setProgress] = useState('');
    const [ready, setReady] = useState(0);  // Segments whose voice is in hand
    const [live, setLive] = useState(-1);   // the Segment Lia is performing right now
    const [take, setTake] = useState(null); // { blob, url, seconds }
    const [tick, setTick] = useState(0);    // seconds into the current phase
    const [tapeSecs, setTapeSecs] = useState(0); // how long the take should run
    const [episodes, setEpisodes] = useState([]);
    const [error, setError] = useState('');

    const day = salesDay ? salesDay.format('YYYY-MM-DD') : '';
    const sameDay = useMemo(() => episodes.filter((e) => e.date === day).length, [episodes, day]);
    const words = useMemo(() => segments.reduce((n, s) => n + s.text.trim().split(/\s+/).length, 0), [segments]);
    const rolling = !!busy && busy !== 'saving';

    const refreshEpisodes = useCallback(() => {
        listEpisodes(KARACA_OFFER_ID).then((r) => setEpisodes(r.data?.episodes || [])).catch(() => {});
    }, []);

    useEffect(() => {
        sceneRef.current = createStudioScene(mountRef.current, { mouthLevel, loadImage });
        refreshEpisodes();
        return () => { stopVoice(); sceneRef.current?.dispose(); sceneRef.current = null; };
    }, [refreshEpisodes]);

    useEffect(() => () => { if (take?.url) URL.revokeObjectURL(take.url); }, [take]);

    // one-second heartbeat while Lia works: rotates the sneak peeks and runs the tape clock
    useEffect(() => {
        if (!rolling) return undefined;
        setTick(0);
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [busy, rolling]);

    // the one click: Script → voices → tape. Each step checks `run` so Durdur cancels it.
    const shoot = async () => {
        const scene = sceneRef.current;
        if (!scene || !day || busy || take) return;
        voiceStream(); // the AudioContext must be born inside this click
        const run = (runRef.current += 1);
        setError('');
        setProgress('');
        setSegments([]);
        setFacts(null);
        setReady(0);
        setLive(-1);
        try {
            setBusy('script');
            setProgress('Lia script yazıyor');
            const { data } = await generateScript({ salesDay: day, notes });
            if (runRef.current !== run) return;
            const segs = (data.segments || []).filter((s) => s.text.trim());
            if (!segs.length) throw new Error('Script boş geldi');
            const script = { segments: segs, tips: data.tips || [] };
            const voice = data.defaultVoice || data.voices?.[0] || '';
            setFacts(data.facts);
            setTitle(data.title);
            setSegments(segs);
            scene.setBoard('opening', data.facts, script);

            setBusy('voices');
            const urls = [];
            for (let i = 0; i < segs.length; i++) {
                setProgress(`Seslendiriliyor · ${KIND_LABEL[segs[i].kind] || segs[i].kind} (${i + 1}/${segs.length})`);
                urls.push(await fetchVoice(voice, segs[i].text.trim()));
                if (runRef.current !== run) return;
                setReady(i + 1);
            }
            // how long the take will run, so the recorder can pick a bitrate that fits the cap
            const secs = await Promise.all(urls.map(voiceSeconds));
            const expected = secs.reduce((t, s) => t + s, 0) + (LEAD_IN_MS + TAIL_MS + GAP_MS * segs.length) / 1000;
            setTapeSecs(expected);
            setProgress('Görseller hazırlanıyor');
            await scene.prepare(data.facts);
            if (runRef.current !== run) return;

            setBusy('recording');
            setProgress('Kayıt başladı — sekmeyi görünür tutun');
            scene.setSegment('opening', data.facts, script);
            scene.setLive(true);
            if (!startRecording(scene.canvas, expected)) throw new Error('Bu tarayıcı MP4 kaydedemiyor (Chrome kullanın)');
            await sleep(LEAD_IN_MS);
            for (let i = 0; i < segs.length; i++) {
                if (runRef.current !== run) break;
                setLive(i);
                scene.setSegment(segs[i].kind, data.facts, script);
                scene.speak(true);
                await playVoice(urls[i]);
                scene.speak(false);
                await sleep(GAP_MS);
            }
            setLive(segs.length); // past the end = holding the closing board
            await sleep(TAIL_MS);
            scene.setLive(false);
            const tape = await stopRecording();
            if (tape && runRef.current === run) setTake({ ...tape, url: URL.createObjectURL(tape.blob) });
        } catch (e) {
            if (isRecording()) await stopRecording();
            scene.setLive(false);
            if (runRef.current !== run) return; // Durdur already cleaned up after itself
            setError(e.response?.data?.error || e.message);
            // ponytail: a failed take leaves nothing usable — clear the half-written Script and
            // send the stage back to idle, so the next Record starts from a clean studio
            setSegments([]);
            setFacts(null);
            setTitle('');
            setReady(0);
            setTapeSecs(0);
            scene.idle();
        } finally {
            scene.speak(false);
            if (runRef.current === run) { setBusy(null); setProgress(''); setLive(-1); }
        }
    };

    const stop = async () => {
        runRef.current += 1;
        stopVoice();
        sceneRef.current?.speak(false);
        sceneRef.current?.setLive(false);
        if (isRecording()) await stopRecording();
        setBusy(null);
        setProgress('');
        setLive(-1);
    };

    const discard = () => { if (take?.url) URL.revokeObjectURL(take.url); setTake(null); };

    const save = async () => {
        if (!take || busy) return;
        setBusy('saving');
        setError('');
        try {
            const fd = new FormData();
            fd.append('video', take.blob, `lia-${day}.mp4`);
            fd.append('offerId', String(KARACA_OFFER_ID));
            fd.append('date', day);
            fd.append('title', title);
            fd.append('transcript', segments.map((s) => s.text.trim()).join('\n\n'));
            await saveEpisode(fd);
            discard();
            setSegments([]);
            setFacts(null);
            refreshEpisodes();
            setProgress(`Episode kaydedildi — ${day} · Episode ${sameDay + 1}`);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setBusy(null);
        }
    };

    const tooBig = !!take && take.blob.size > MAX_MB * 1e6;
    const c = facts?.compare;
    const n = segments.length;
    const kinds = n ? segments.map((s) => s.kind) : Object.keys(KIND_LABEL);
    const label = (i) => KIND_LABEL[kinds[i]] || kinds[i];
    const STEP = { totals: 'Toplam', influencers: 'Influencer', tips: 'Tips' }; // fits the six bars on a narrow panel
    const stepState = (i) => {
        if (busy === 'recording') return i < live ? 'done' : i === live ? 'active' : 'ready';
        if (busy === 'voices') return i < ready ? 'ready' : i === ready ? 'active' : 'pending';
        return 'pending';
    };
    const status = busy === 'script' ? { tag: 'Lia yazıyor', peek: `Lia ${WRITING_PEEKS[Math.floor(tick / 3) % WRITING_PEEKS.length]}…` }
        : busy === 'voices' ? (ready < n
            ? { tag: `Seslendiriliyor · ${label(ready)} · ${ready + 1}/${n}`, peek: peek(segments[ready].text), quote: true }
            : { tag: 'Görseller hazırlanıyor', peek: 'Ürün fotoğrafları duvar ekranına yükleniyor…' })
        : busy === 'recording' ? (live < 0 ? { tag: 'Kayıt', peek: 'Işıklar yanıyor, Lia yerini alıyor…' }
            : live >= n ? { tag: 'Kayıt', peek: 'Kapanış görüntüsü tutuluyor…' }
            : { tag: `On air · ${label(live)} · ${live + 1}/${n}`, peek: peek(segments[live].text), quote: true })
        : null;
    return (
        <div className={styles.grid}>
            <div className={styles.stageColumn}>
                <div ref={mountRef} className={styles.stage} />
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                    {rolling
                        ? <Button variant="contained" color="error" size="large" className={styles.record} startIcon={<StopCircleOutlined />} onClick={stop}>Durdur</Button>
                        : <Button variant="contained" color="error" size="large" className={styles.record} startIcon={<FiberManualRecord />} disabled={!day || !!busy || !!take || !canTapeMp4()} onClick={shoot}>Record</Button>}
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        {rolling ? progress : take ? 'Take hazır — Episode olarak kaydet ya da vazgeç' : `${day} · tek tıkla script, ses ve kayıt`}
                    </Typography>
                </Stack>
                {!busy && progress && <Alert severity="success" sx={{ mt: 1 }}>{progress}</Alert>}
                {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
                {!canTapeMp4() && <Alert severity="warning" sx={{ mt: 1 }}>Bu tarayıcı MP4 kaydedemiyor; Episode'lar MP4 olarak saklanır. Chrome kullanın.</Alert>}
                {take && (
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Take önizleme · {take.seconds} sn · {(take.blob.size / 1e6).toFixed(1)} MB · MP4</Typography>
                            <video src={take.url} controls className={styles.preview} />
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Button variant="contained" startIcon={<SaveOutlined />} disabled={busy === 'saving' || tooBig} onClick={save}>Episode olarak kaydet</Button>
                                <Button variant="outlined" color="inherit" startIcon={<DeleteOutline />} disabled={busy === 'saving'} onClick={discard}>Vazgeç</Button>
                            </Stack>
                            {busy === 'saving' && <LinearProgress sx={{ mt: 1.5 }} />}
                            {tooBig && <Alert severity="error" sx={{ mt: 1 }}>Video {MAX_MB} MB sınırını aşıyor. Yeniden kaydedin.</Alert>}
                        </CardContent>
                    </Card>
                )}
            </div>
            <div className={styles.controls}>
                <Card>
                    <CardContent>
                        <Stack spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker label="Sales Day" value={salesDay} maxDate={dayjs()} format="DD.MM.YYYY" disabled={rolling}
                                    onChange={(v) => setSalesDay(v)} slotProps={{ textField: { size: 'small' } }} />
                            </LocalizationProvider>
                            <TextField label="Producer Notes (opsiyonel)" multiline minRows={2} size="small" value={notes} disabled={rolling}
                                onChange={(e) => setNotes(e.target.value)} placeholder="Örn. dün Karaca'da kampanya vardı; düşüşe takılma, ileriye bak" />
                            {sameDay > 0 && !take && <Alert severity="info">{day} için {sameDay} Episode var; bu kayıt Episode {sameDay + 1} olarak yayına girer.</Alert>}
                        </Stack>
                    </CardContent>
                </Card>

                {(rolling || n > 0) && (
                    <div className={styles.prompter}>
                        <div className={styles.prompterHead}>
                            <span>Prompter · Lia</span>
                            <span>{facts?.salesDayLabel || day}</span>
                        </div>
                        {status ? (
                            <>
                                <div className={styles.status}><i className={styles.pulse} />{status.tag}</div>
                                <p key={status.peek} className={`${styles.peek} ${status.quote ? styles.quote : ''}`}>{status.peek}</p>
                                <div className={styles.steps}>
                                    {kinds.map((k, i) => (
                                        <span key={k} className={`${styles.step} ${styles[stepState(i)] || ''}`}><i /><small>{STEP[k] || label(i)}</small></span>
                                    ))}
                                </div>
                                {busy === 'recording' && <div className={styles.clock}>{mmss(tick)} / ≈{mmss(tapeSecs)}</div>}
                            </>
                        ) : facts && (
                            <>
                                <h3 className={styles.prompterTitle}>{title}</h3>
                                <div className={styles.factRow}>
                                    <span className={styles.fact}><b>{TL(facts.totals.sales)}</b> satış</span>
                                    <span className={styles.fact}><b>{facts.totals.orders}</b> sipariş</span>
                                    {facts.traffic && (
                                        <>
                                            <span className={styles.fact}><b>{facts.traffic.clicks.toLocaleString('tr-TR')}</b> tıklama</span>
                                            <span className={styles.fact}><b>{facts.traffic.conversions.toLocaleString('tr-TR')}</b> dönüşüm</span>
                                            <span className={styles.fact}><b>{facts.traffic.conversionRate ?? '—'}%</b> dönüşüm oranı</span>
                                        </>
                                    )}
                                    <span className={`${styles.fact} ${(c.deltaVsPreviousDayPct ?? 0) >= 0 ? styles.up : styles.down}`}><b>{pct(c.deltaVsPreviousDayPct)}</b> düne göre</span>
                                    <span className={`${styles.fact} ${(c.deltaVsAvg7Pct ?? 0) >= 0 ? styles.up : styles.down}`}><b>{pct(c.deltaVsAvg7Pct)}</b> 7g ort.</span>
                                </div>
                                <details className={styles.script}>
                                    <summary>Tam script · {words} kelime · ≈ {Math.round(words / 2.6)} sn</summary>
                                    <ol className={styles.lines}>
                                        {segments.map((s, i) => (
                                            <li key={s.kind} className={styles.line}>
                                                <div className={styles.lineHead}><span>{String(i + 1).padStart(2, '0')} · {KIND_LABEL[s.kind] || s.kind}</span></div>
                                                <p>{s.text}</p>
                                            </li>
                                        ))}
                                    </ol>
                                </details>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InfluioTvStudio;
